import { Eyebrow } from '@bp-files/ui';
import { CardGrid, NavCard } from '@/components/CardGrid';
import { SurfaceHeader } from '@/surfaces/SurfaceHeader';
import { SETTINGS_INDEX_COPY } from '@/components/Settings/settingsIndex.copy';
import { settingsGroups } from '@/surfaces/surfaces';

/**
 * `/settings` — every settings page, as the same card grid Files uses.
 *
 * **It exists because the sidebar does not exist on a phone.** `AppShell`
 * renders the 236px nav `hidden … md:block` and the 64px rail
 * `hidden … sm:flex`: under 768px the Settings sidebar is off the screen, under
 * 640px so is the gear that switches into the domain, and every settings page
 * was reachable only by typing its URL. The account icon is the whole of the
 * chrome down there, so the account menu is where the door has to be
 * (`AccountMenu` in `App.tsx`).
 *
 * **It renders `settingsGroups()` — the sidebar's own function — and holds no
 * list of its own.** That is what keeps this on the right side of **ADR-0064**,
 * which took Secure sign-in and Your signature *out* of the account menu on the
 * grounds that two named links to one page is two places to keep in agreement.
 * The argument was about pages. This is one link to the domain, and the page it
 * lands on cannot name a destination the sidebar does not, or call it something
 * else, because both read the same array.
 *
 * **Not hidden above `md`.** A page that existed at one width would be a page
 * nobody could link to, and the reader who followed the account menu on a
 * laptop would land on a redirect instead of a screen. The redundancy with the
 * sidebar beside it is the cost, and it is smaller than the alternative.
 *
 * `isAdmin` drives which groups render, and it is **visibility only** — SEC-5
 * puts authorization in the API, and `AdminOnly` still gates each admin route
 * on arrival.
 */
export function SettingsIndex({ isAdmin }: { isAdmin: boolean }) {
  return (
    <SurfaceHeader title={SETTINGS_INDEX_COPY.title} summary={SETTINGS_INDEX_COPY.summary}>
      <div className="flex flex-col gap-6">
        {settingsGroups(isAdmin).map((group) => (
          <section key={group.id} className="flex flex-col gap-3">
            {/*
              `as="h2"` — a member sees one group and an admin sees two, and two
              unlabelled grids stacked on one page read as a single list whose
              second half is unexplained. `Eyebrow` owns the uppercase and the
              tracking, so the copy stays sentence case (`APP_COPY.nav.groups`,
              shared with the sidebar's own headings).
            */}
            <Eyebrow as="h2">{group.label}</Eyebrow>

            <CardGrid label={group.label}>
              {group.entries.map((entry) => (
                <li key={entry.id}>
                  <NavCard
                    to={entry.path}
                    name={entry.label}
                    detail={
                      SETTINGS_INDEX_COPY.details[
                        entry.id as keyof typeof SETTINGS_INDEX_COPY.details
                      ]
                    }
                    /*
                      No glyph and no meta line. `CardBody` treats both as
                      slots, and a settings page has neither a kind worth a
                      gutter icon nor a date worth an amber line — the two
                      things a Files tile spends them on. An icon per entry
                      would be seven new glyphs saying what seven names
                      already say.
                    */
                    meta={null}
                  />
                </li>
              ))}
            </CardGrid>
          </section>
        ))}
      </div>
    </SurfaceHeader>
  );
}
